import { useEffect, useState, type FormEvent } from "react";
import { FOCUS_AREAS, FOCUS_LABELS, type Weekday } from "@coaching/types";
import { EVERY_DAY, WEEKDAY_LABELS, normaliseWorkingDays } from "@coaching/workingDays";
import { COACH_NAME_MAX, initialsOf } from "@coaching/coachDisplay";
import { CONTACT_EMAIL_MAX, parseContactEmail, parsePhone } from "@coaching/contact";
import { PRICE_TIERS, isPriceTier, type PriceTier } from "@coaching/price";
import { DEFAULT_COACHING_CONFIG } from "@coaching/config";
import { FeesNote, TierSplits } from "../components/Money";
import { PromiseNote, useCoachingConfig } from "../components/PromiseNote";
import { TagInput } from "../components/TagInput";
import { api, ApiError, watchAppPhoto, watchCoach, type Coach } from "../api";
import { useCoach } from "../auth";
import { Centred, ErrorState } from "../components/States";

interface Form {
    displayName: string;
    contactEmail: string;
    contactPhone: string;
    priceTier: PriceTier | null;
    headline: string;
    bio: string;
    experienceYears: string;
    specialties: string[];
    certifications: string[];
    languages: string[];
    acceptingSubmissions: boolean;
    maxOpen: string;
    turnaroundDays: number;
    workingDays: Weekday[];
}

const HEADLINE_MAX = 80;
const BIO_MAX = 2000;
const SKILL_SUGGESTIONS = FOCUS_AREAS.filter((a) => a !== "other").map((a) => FOCUS_LABELS[a]).concat(["Bandeja", "Vibora", "Chiquita", "Doubles tactics", "Beginners", "Juniors"]);
const LANGUAGE_SUGGESTIONS = ["English", "Spanish", "Portuguese", "Italian", "French"];
/** Monday first, as a UK coach reads a week. */
const WEEK: Weekday[] = [1, 2, 3, 4, 5, 6, 0];

export default function Profile() {
    const { user, clubs } = useCoach();
    const cfg = useCoachingConfig();
    const [coach, setCoach] = useState<Coach | null | undefined>(undefined);
    const [error, setError] = useState<string | null>(null);
    const [form, setForm] = useState<Form | null>(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const [photo, setPhoto] = useState<string | null | undefined>(undefined);
    const [photoFailed, setPhotoFailed] = useState(false);

    useEffect(() => watchCoach(user.uid, setCoach, (e) => setError(e.message)), [user.uid]);
    useEffect(() => watchAppPhoto(user.uid, (url) => { setPhoto(url); setPhotoFailed(false); }, (e) => {
        console.warn("app photo subscription failed", { uid: user.uid, code: (e as { code?: string }).code });
        setPhoto(null);
        setPhotoFailed(true);
    }), [user.uid]);
    useEffect(() => {
        if (coach && form === null) {
            setForm({
                displayName: coach.displayName,
                contactEmail: coach.contactEmail ?? user.email ?? "",
                contactPhone: coach.contactPhone ?? "",
                priceTier: isPriceTier(coach.priceTier) ? coach.priceTier : null,
                headline: coach.headline, bio: coach.bio,
                experienceYears: coach.experienceYears === null ? "" : String(coach.experienceYears),
                specialties: coach.specialties, certifications: coach.certifications, languages: coach.languages,
                acceptingSubmissions: coach.acceptingSubmissions,
                maxOpen: coach.maxOpen === null ? "" : String(coach.maxOpen),
                turnaroundDays: coach.turnaroundDays ?? cfg?.defaultTurnaroundDays ?? 3,
                workingDays: normaliseWorkingDays(coach.workingDays),
            });
        }
    }, [coach, form, cfg, user.email]);

    if (error) return <ErrorState title="Could not load your profile" body={error} />;
    if (coach === undefined || form === null) return <Centred spinner>Loading your profile</Centred>;
    if (coach === null) return <ErrorState title="No coach profile yet" body="Your club attachment exists but the profile shell is missing. Ask Swana to re-run the coach setup." />;

    const maxDays = cfg?.maxTurnaroundDays ?? 7;
    const fees = cfg ?? DEFAULT_COACHING_CONFIG;
    const set = <K extends keyof Form>(k: K, v: Form[K]) => { setForm({ ...form, [k]: v }); setSaved(false); };
    const toggleDay = (d: Weekday) => {
        const on = form.workingDays.includes(d);
        if (on && form.workingDays.length === 1) return;
        set("workingDays", on ? form.workingDays.filter((x) => x !== d) : [...form.workingDays, d].sort((a, b) => a - b));
    };

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        if (form.displayName.trim() === "") { setSaveError("Add the name players will see."); return; }
        if (parseContactEmail(form.contactEmail) === null) { setSaveError("Add an email address players can contact you on, like name@club.co.uk."); return; }
        if (form.contactPhone.trim() !== "" && parsePhone(form.contactPhone) === null) { setSaveError("Check the phone number: digits, spaces and a leading + only."); return; }
        setSaving(true); setSaveError(null);
        try {
            await api.updateProfile({
                displayName: form.displayName.trim(),
                contactEmail: form.contactEmail.trim(),
                contactPhone: form.contactPhone.trim() === "" ? null : form.contactPhone.trim(),
                priceTier: form.priceTier,
                headline: form.headline.trim(), bio: form.bio.trim(),
                experienceYears: form.experienceYears.trim() === "" ? null : Number(form.experienceYears),
                specialties: form.specialties, certifications: form.certifications, languages: form.languages,
                acceptingSubmissions: form.acceptingSubmissions,
                maxOpen: form.maxOpen.trim() === "" ? null : Number(form.maxOpen),
                turnaroundDays: form.turnaroundDays,
                workingDays: form.workingDays.length === 0 ? [...EVERY_DAY] : form.workingDays,
            });
            setSaved(true);
        } catch (err) {
            setSaveError(err instanceof ApiError ? err.message : "Could not save. Check your connection and try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form className="profile" onSubmit={submit}>
            <div className="page-head">
                <h1>Profile</h1>
                <p className="muted">What players see when they choose a coach at {clubs.map((c) => c.clubName).join(" and ")}.</p>
            </div>

            <div className="profile-grid">
                <div className="profile-col">
                <section className="card preview" aria-label="How players see you">
                    <Avatar url={photo} name={form.displayName} />
                    <div className="preview-text">
                        <p className="preview-name">{form.displayName.trim() || "Add your name below"}</p>
                        <p className="muted preview-headline">{form.headline.trim() || "Add a headline below"}</p>
                        <p className="hint-text">
                            {photoFailed ? "Your photo could not be loaded. Refresh to try again."
                                : photo === null ? "Add a profile photo in the Swana app and it shows here and to players."
                                : "Your photo comes from the Swana app. Change it there and it updates here and for players."}
                        </p>
                    </div>
                </section>

                <section className="card">
                    <h2 className="eyebrow">Availability</h2>
                    <label className="switch">
                        <input type="checkbox" checked={form.acceptingSubmissions} onChange={(e) => set("acceptingSubmissions", e.target.checked)} />
                        <span>Accepting new clips</span>
                    </label>
                    <div className="two-up">
                        <div className="field">
                            <div className="field-head"><label htmlFor="turnaround">Review turnaround</label></div>
                            <select id="turnaround" value={form.turnaroundDays} onChange={(e) => set("turnaroundDays", Number(e.target.value))}>
                                {Array.from({ length: maxDays }, (_, i) => i + 1).map((n) => (
                                    <option key={n} value={n}>{n} working {n === 1 ? "day" : "days"}</option>
                                ))}
                            </select>
                            <p className="hint-text">Counted from the day after the player sends, on the days you work.</p>
                        </div>
                        <div className="field">
                            <div className="field-head"><label htmlFor="maxOpen">Clips in your inbox at once</label></div>
                            <input id="maxOpen" type="number" min={1} max={1000} value={form.maxOpen} onChange={(e) => set("maxOpen", e.target.value)} placeholder={cfg ? `${cfg.defaultCoachMaxOpen}` : ""} />
                            <p className="hint-text">Requests waiting for your answer and those you are reviewing, {coach.openCount} now. When your inbox is full, players see you as fully booked until you return or decline one. Leave empty for the club default.</p>
                        </div>
                    </div>
                    <div className="field">
                        <div className="field-head"><span className="field-label">Working days</span></div>
                        <div className="days" role="group" aria-label="Working days">
                            {WEEK.map((d) => {
                                const on = form.workingDays.includes(d);
                                return (
                                    <button type="button" key={d} className={`day${on ? " on" : ""}`} aria-pressed={on} onClick={() => toggleDay(d)}>
                                        {WEEKDAY_LABELS[d]}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="hint-text">Requests sent on a day off start counting from your next working day.</p>
                    </div>
                    <PromiseNote turnaroundDays={form.turnaroundDays} workingDays={form.workingDays} cfg={cfg} />
                </section>

                <section className="card">
                    <h2 className="eyebrow">Your price</h2>
                    <div className="tiers" role="radiogroup" aria-label="Price per review">
                        {PRICE_TIERS.map((p) => (
                            <button type="button" key={p} role="radio" aria-checked={form.priceTier === p}
                                className={`tier${form.priceTier === p ? " on" : ""}`} onClick={() => set("priceTier", p)}>
                                £{(p / 100).toFixed(2)}
                            </button>
                        ))}
                    </div>
                    <p className="hint-text">
                        {form.priceTier === null ? "Not set yet: players see your club's price until you choose one. " : ""}
                        What a player pays for one review, in the app when they send it. Here is where each price goes:
                    </p>
                    <TierSplits cfg={fees} selected={form.priceTier} />
                    <FeesNote cfg={fees} />
                </section>

                <section className="card">
                    <h2 className="eyebrow">Contact for players</h2>
                    <div className="two-up">
                        <div className="field">
                            <div className="field-head"><label htmlFor="contactEmail">Email</label></div>
                            <input id="contactEmail" type="email" required maxLength={CONTACT_EMAIL_MAX} value={form.contactEmail} onChange={(e) => set("contactEmail", e.target.value)} placeholder="name@club.co.uk" autoComplete="email" />
                        </div>
                        <div className="field">
                            <div className="field-head"><label htmlFor="contactPhone">Phone <span className="muted">(optional)</span></label></div>
                            <input id="contactPhone" type="tel" maxLength={24} value={form.contactPhone} onChange={(e) => set("contactPhone", e.target.value)} placeholder="+44 7700 900000" autoComplete="tel" />
                        </div>
                    </div>
                    <p className="hint-text">Only players who have sent you a request see these, on their confirmation, so they can ask you a question.</p>
                </section>

                </div>
                <div className="profile-col">
                <section className="card">
                    <h2 className="eyebrow">About you</h2>
                    <div className="field">
                        <div className="field-head">
                            <label htmlFor="displayName">Name</label>
                            <span className="counter" aria-hidden>{form.displayName.length} / {COACH_NAME_MAX}</span>
                        </div>
                        <input id="displayName" maxLength={COACH_NAME_MAX} required value={form.displayName} onChange={(e) => set("displayName", e.target.value)} placeholder="Your first and last name" />
                        <p className="hint-text">The name players see in the coach list and on their reviews.</p>
                    </div>
                    <div className="field">
                        <div className="field-head">
                            <label htmlFor="headline">Headline</label>
                            <span className="counter" aria-hidden>{form.headline.length} / {HEADLINE_MAX}</span>
                        </div>
                        <input id="headline" maxLength={HEADLINE_MAX} value={form.headline} onChange={(e) => set("headline", e.target.value)} placeholder="Head coach, ex-national junior" />
                        <p className="hint-text">One line under your name in the coach list.</p>
                    </div>
                    <div className="field">
                        <div className="field-head">
                            <label htmlFor="bio">Bio</label>
                            <span className="counter" aria-hidden>{form.bio.length} / {BIO_MAX}</span>
                        </div>
                        <textarea id="bio" rows={6} maxLength={BIO_MAX} value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="How you coach, who you work with, what players can expect from a review." />
                        <p className="hint-text">Shown when a player taps your name for more.</p>
                    </div>
                    <div className="field narrow">
                        <div className="field-head"><label htmlFor="years">Years coaching</label></div>
                        <input id="years" type="number" min={0} max={60} value={form.experienceYears} onChange={(e) => set("experienceYears", e.target.value)} />
                    </div>
                </section>

                <section className="card">
                    <h2 className="eyebrow">Expertise</h2>
                    <TagInput label="Key skills" value={form.specialties} onChange={(v) => set("specialties", v)} placeholder="Bandeja, net play, doubles tactics"
                        suggestions={SKILL_SUGGESTIONS} hint="What you are known for. Players see these as 'Known for'." />
                    <TagInput label="Qualifications" value={form.certifications} onChange={(v) => set("certifications", v)} placeholder="LTA Level 3, FIP Coach"
                        hint="Press Enter after each one." />
                    <TagInput label="Languages" value={form.languages} onChange={(v) => set("languages", v)} placeholder="English" suggestions={LANGUAGE_SUGGESTIONS} />
                </section>
                </div>
            </div>

            {saveError && <p className="error-text" role="alert">{saveError}</p>}
            <div className="form-actions">
                <button className="btn primary" type="submit" disabled={saving}>{saving ? "Saving" : "Save profile"}</button>
                {saved && <span className="ok-text">Saved</span>}
            </div>
        </form>
    );
}

function Avatar({ url, name }: { url: string | null | undefined; name: string }) {
    const [broken, setBroken] = useState(false);
    useEffect(() => setBroken(false), [url]);
    const initials = initialsOf(name);
    if (url === undefined) return <div className="avatar skeleton" aria-hidden />;
    if (url === null || broken) return <div className="avatar" aria-hidden>{initials}</div>;
    return <img className="avatar" src={url} alt="" onError={() => setBroken(true)} />;
}
